import { useNavigation } from '@react-navigation/native';
import { Headline2, Headline3, theme } from 'aimo-ui';
import MenuItem from 'components/common/MenuItem';
import Icon from 'components/icons/Icon';
import ChargerBadge from 'components/park/ChargerBadge';
import useParkingZoneWithEVCheck from 'hooks/useParkingZoneWithEVCheck';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { parkingVehicleLicensePlate } from 'states/common';
import { useActiveElementStore } from 'stores/activeElementStore';
import { useAuthStore } from 'stores/authStore';
import { useChargingStore } from 'stores/chargingStore';
import { useParkingStore } from 'stores/parkingStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { useUiStore } from 'stores/uiStore';
import { SheetDetailsWrapper, StyledWrapper } from 'styles/ContainerStyles';
import { ParkNavProps } from 'types/navigation';
import { isWeb } from 'utils/deviceUtils';
import Logger from 'utils/Logger';
import { makePoint, openDirections } from 'utils/mapUtils';

const OngoingParkingZoneDetails = ({
  parkingZoneUid,
  licensePlate,
}: {
  parkingZoneUid: string;
  licensePlate?: string | null;
}) => {
  const { setParkingConditions } = useParkingStore((state) => state);
  const selectedOngoingSessionType = useParkingStore(
    (state) => state.selectedOngoingSession
  );
  const ongoingChargingSessionId = useChargingStore(
    (state) => state.ongoingChargingSession
  );
  const isUserRestricted = useAuthStore((state) => state.accountRestricted);
  const { t } = useTranslation();
  const { navigate: navigatePark } = useNavigation<ParkNavProps>();

  const { setParkingVehiclePlate } = useParkingStore((state) => state);
  const parkingZoneResult = useParkingZoneWithEVCheck(
    parkingZoneUid,
    'cache-and-network',
    true
  );
  const { data, loading } = parkingZoneResult;

  const {
    address,
    commercialZoneView,
    locationType,
    countryCode,
    name,
    info,
    parkingZoneChargingStations,
    parkingMethods,
  } = data?.parkZone || {};
  const { street, number, city, marker } = address || {};
  const zoneName = `${commercialZoneView?.zoneCode ?? ''} ${
    commercialZoneView?.name ?? ''
  }`;

  const zoneAddress = `${street ?? ''} ${number ?? ''} ${city ?? ''}`;

  const availableSpots =
    parkingZoneChargingStations?.reduce((acc, station) => {
      return (
        acc +
        (station?.evses?.filter((evse) =>
          ['AVAILABLE', 'OCCUPIED'].includes(evse.status ?? '')
        ).length || 0)
      );
    }, 0) || 0;

  const handleOpenDirections = () => {
    if (marker?.coordinates) {
      openDirections(name, makePoint(marker.coordinates));
    }
  };

  const handleStartChargingProcess = () => {
    if (isUserRestricted) {
      return useUiStore.getState().setSheetModal('accountRestricted');
    }
    useActiveElementStore.getState().setActiveElement(parkingZoneUid);
    setParkingConditions({ selectedParkingZoneId: parkingZoneUid });
    useParkzoneStore.getState().setSelectedParkingZoneTab('CHARGE');
    useParkzoneStore
      .getState()
      .setSelectedChargingPricingInfoSegmentTariffPlanId(undefined);
    navigatePark('chargeSelect', { parkingZoneUid });
  };

  useEffect(() => {
    // If current parking zone has charging station and is unmanaged, set the active element to be used in the hook
    if (
      parkingZoneChargingStations &&
      parkingZoneChargingStations?.length > 0
    ) {
      useActiveElementStore
        .getState()
        .setActiveElementTypeAndStation(
          !parkingMethods?.unmanagedParking ? 'PARK_AND_CHARGE' : 'CHARGING',
          parkingZoneChargingStations?.[0]?.id ?? ''
        );
    }
  }, [parkingZoneChargingStations, parkingMethods?.unmanagedParking]);

  useEffect(() => {
    if (licensePlate) {
      parkingVehicleLicensePlate(licensePlate);
      setParkingVehiclePlate(licensePlate);
    } else {
      Logger.error(
        `Ongoing session in park zone ${parkingZoneUid} is missing the accessDevice (licensePlate: ${licensePlate})`
      );
    }
  }, [licensePlate, parkingZoneUid, setParkingVehiclePlate]);

  const seStreetZone = locationType === 'STREET' && countryCode === 'SE';
  return (
    <SheetDetailsWrapper resetPadding={isWeb} pb={20}>
      <StyledWrapper mb={25} mt={0}>
        <Headline3 center testID="ongoing-parking-zone-details">
          {t('parking.additionalOptions')}
        </Headline3>
      </StyledWrapper>
      {info?.doorCode !== '' && info?.doorCode !== null && (
        <StyledWrapper>
          <MenuItem
            menuLabel={t('parking.garageEntryCode')}
            iconName="Unlocked-Outlined"
            paddingVertical={12}
            testID="ongoing-parking-doorcode-title"
            background={theme.colors.gray50}
            large
            rightComponent={<Headline2>{info?.doorCode}</Headline2>}
          />
        </StyledWrapper>
      )}
      {parkingZoneChargingStations &&
        parkingZoneChargingStations.length > 0 &&
        selectedOngoingSessionType !== 'CHARGING' &&
        !ongoingChargingSessionId &&
        licensePlate && (
          <StyledWrapper>
            <MenuItem
              menuLabel={t('evCharging.chargeYourCar')}
              iconName="Charging"
              paddingVertical={12}
              testID="ongoing-parking-charge"
              onPress={() => handleStartChargingProcess()}
              background={theme.colors.lightBg.green}
              large
              withArrow
              rightComponent={
                <ChargerBadge
                  availableSpots={availableSpots}
                  loading={loading}
                  isCollapsed
                />
              }
            />
          </StyledWrapper>
        )}
      {(seStreetZone || locationType !== 'STREET') && (
        <StyledWrapper mb={30}>
          <MenuItem
            menuLabel={zoneName}
            subtitle={zoneAddress}
            iconName="Pin-Outlined"
            paddingVertical={12}
            testID="ongoing-parking-directions"
            onPress={handleOpenDirections}
            rightComponent={<Icon name={'Directions-Outlined'} size="l" />}
            background={theme.colors.gray50}
            large
          />
        </StyledWrapper>
      )}
    </SheetDetailsWrapper>
  );
};

export default OngoingParkingZoneDetails;
